import {
  RUNNER_TYPES,
  type RunnerType as CoreRunnerType,
  type RunnerDispatchPolicyEntityType,
} from '@agentteams/core-constants';
export type { RunnerDispatchPolicyEntityType } from '@agentteams/core-constants';
export type DaemonTriggerResolvedSettings = {
  runnerType: DaemonTriggerResolvedField<string>;
  model: {
    value: string | null;
    source: DaemonTriggerResolveSource;
    status: 'RESOLVED' | 'CLIENT_DEFAULT' | 'INVALID';
    requestedValue?: string | null;
    fastModeSupported: boolean | null;
    supportedEffortLevels: string[];
  };
  fastMode: DaemonTriggerResolvedField<boolean>;
  effort: DaemonTriggerResolvedField<string | null>;
  planMode: DaemonTriggerResolvedField<boolean>;
  useWorktree: DaemonTriggerResolvedField<boolean>;
  targetDaemonId: DaemonTriggerResolvedField<string | null>;
  isExecutable: boolean;
  reasons: Array<{ field: string; source: DaemonTriggerResolveSource; message: string }>;
  errors: Array<{ detail: string }>;
};

export type DaemonTriggerResolvedField<T> = {
  value: T;
  source: DaemonTriggerResolveSource;
  requestedValue?: T | null;
};

export type DaemonTriggerResolveSource = 'TRIGGER' | 'SYSTEM' | 'MEMBER' | 'AGENT' | 'AGENT_FORCED' | 'DEFAULT';

export type RunnerDispatchPolicy = {
  id: string;
  projectId: string;
  entityType: RunnerDispatchPolicyEntityType;
  entityState: string;
  promptPresetId: string;
  sortOrder: number;
  /** 시스템 기본 정책 카탈로그 키. null이면 사용자 생성 정책. */
  systemKey: string | null;
  systemVersion: number | null;
  /** 사용자가 수정해 기본값 동기화에서 제외된 시스템 정책. */
  isSystemModified: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  promptPreset: RunnerDispatchPolicyPromptPreset;
};

export type RunnerDispatchPolicyPromptPreset = {
  id: string;
  projectId: string;
  name: string;
  prompt: object;
  userConventionIds: string[];
  planMode: boolean;
  kind: 'DISPATCH_POLICY';
  createdByMemberId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type RunnerDispatchPolicyRecommendation = {
  policyId: string;
  reason: 'PLAN_BASED' | 'CRITICAL_FINDINGS' | 'STANDALONE';
  /** 추천 기본 정책이 비활성화/삭제되어 목록 첫 정책으로 폴백했는지 여부. */
  isFallback: boolean;
} | null;

export type PromptPreset = {
  id: string;
  projectId: string;
  name: string;
  prompt: object;
  userConventionIds: string[];
  planMode: boolean;
  isShared: boolean;
  createdByMemberId: string;
  createdByMemberName: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type CreatePromptPresetInput = {
  name: string;
  prompt: object;
  userConventionIds?: string[];
  planMode?: boolean;
  isShared?: boolean;
};

export type UpdatePromptPresetInput = {
  name?: string;
  prompt?: object;
  userConventionIds?: string[];
  planMode?: boolean;
  isShared?: boolean;
};

// ---------------------------------------------------------------------------
// 러너 요청 엔티티 (원본: web/src/lib/api/daemon.ts·agentConfigs.ts·projects.ts·
// runnerModels.ts — 타입·순수 값만 공용으로 둔다. fetch 클라이언트는 web에 남는다)
// ---------------------------------------------------------------------------

export const osTypeValues = ['MACOS', 'LINUX', 'WINDOWS'] as const;
export type OsType = (typeof osTypeValues)[number];

// 러너 타입 값의 단일 진실 소스는 @agentteams/core-constants(RUNNER_TYPES)이다.
// 여기서는 로컬에 문자열을 나열하지 않고 SSOT 값을 사용자 노출 정렬 순서대로 배열한다.
// (라벨/정렬은 이 튜플에서만 관리 — 값 추가/삭제/개명은 SSOT에서 수행)
export const runnerTypeValues = [
  RUNNER_TYPES.CLAUDE_CODE,
  RUNNER_TYPES.CODEX,
  RUNNER_TYPES.ANTIGRAVITY,
  RUNNER_TYPES.OPENCODE,
  RUNNER_TYPES.AMP,
  RUNNER_TYPES.COPILOT_CLI,
  RUNNER_TYPES.CURSOR_CLI,
  RUNNER_TYPES.KIMI_CLI,
  RUNNER_TYPES.KIRO_CLI,
  RUNNER_TYPES.GROK_BUILD,
  RUNNER_TYPES.OMP,
  RUNNER_TYPES.MUSE_CODE,
] as const;
export type RunnerType = CoreRunnerType;

/** 러너 자동 업데이트 설치 실패 원인. API의 `updateFailedReason`과 값이 일치해야 한다. */
export type RunnerUpdateFailureReason = 'PERMISSION_DENIED' | 'UNKNOWN';

export type Daemon = {
  id: string;
  memberId: string;
  label: string | null;
  osType: OsType | null;
  runnerVersion: string | null;
  pendingVersion: string | null;
  /** 자동 업데이트 설치가 막힌 상태. 서버가 성공 보고나 새 버전 하트비트를 받으면 함께 해제한다. */
  updateFailedPackage?: 'cli' | 'runner' | null;
  updateFailedVersion?: string | null;
  updateFailedReason?: RunnerUpdateFailureReason | null;
  updateFailedAt?: string | null;
  /** 러너가 보고한 설치 실패 원문. 소유자 본인 목록에서만 내려온다(공유 러너에는 없음). */
  updateFailedMessage?: string | null;
  /** 이 러너가 도는 머신 식별자. 소유자 본인 목록에서만 내려온다(공유 러너에는 없음). */
  machineId?: string | null;
  supportedEngines: RunnerType[];
  detectedEngines: RunnerType[];
  detectedEnginesAt: string | null;
  sortOrder: number;
  lastSeenAt: string | null;
  createdAt: string;
  updatedAt: string;
};

/// 대기 순번이 붙은 요청의 대상 러너 상태. 서버 runnerQueue.QueueRunnerState와 1:1로 대응한다.
export type QueueRunnerState = 'IDLE' | 'RUNNING' | 'OFFLINE';
export type DaemonTriggerChainKind = 'RETRY' | 'PLAN_TASK';

export type DaemonTokenUsage = {
  status: 'COMPLETE' | 'PARTIAL' | 'MISSING' | 'UNSUPPORTED';
  scope: 'MAIN_LOOP';
  inputTokens: number | null;
  outputTokens: number | null;
  cacheReadInputTokens: number | null;
  cacheCreationInputTokens: number | null;
};

export type DaemonTrigger = {
  id: string;
  prompt: object | string;
  runnerType: string;
  model: string | null;
  fastMode: boolean;
  /// 이 실행에 확정된 추론 강도(Effort) 레벨. null이면 모델/클라이언트 기본값을 사용합니다.
  effort: string | null;
  status:
    | 'PENDING'
    | 'IN_PROGRESS'
    | 'CANCEL_REQUESTED'
    | 'DONE'
    | 'CANCELLED'
    | 'FAILED'
    | 'REJECTED'
    | 'NEEDS_REVIEW';
  agentConfigId: string;
  /// 요청이 귀속된 프로젝트 ID. 공유 러너 사용 시 agentConfig가 위치한 프로젝트와 다를 수 있습니다.
  projectId: string;
  startedAt: string | null;
  /// 구버전 API와 미보고 실행은 사용량이 없다. 미수집을 실제 0으로 표시하지 않는다.
  tokenUsage?: DaemonTokenUsage | null;
  errorMessage: string | null;
  worktreeError: string | null;
  lastHeartbeatAt: string | null;
  conversationId: string | null;
  chainId: string | null;
  parentTriggerId: string | null;
  chainKind: DaemonTriggerChainKind | null;
  chainPlanId: string | null;
  chainPlanTitle: string | null;
  chainTaskDoneCount: number | null;
  chainTaskTotalCount: number | null;
  chainCurrentTaskTitle: string | null;
  chainAttemptCount: number | null;
  createdByMemberId: string;
  createdByName: string | null;
  targetDaemonId: string | null;
  planMode: boolean;
  runnerDispatchPolicyId?: string | null;
  claimedByDaemonId: string | null;
  useWorktree: boolean;
  repositoryId: string | null;
  /// 소유 러너 PENDING 대기열에서의 순번(1부터). 종료 상태·공유 러너·미지정 요청은 null.
  /// ⚠️ 목록 응답에서만 계산된다. 단건 조회·생성 응답은 PENDING이어도 항상 null이다.
  queuePosition: number | null;
  /// 순번이 붙은 요청의 대상 러너 상태. queuePosition과 같은 제약(목록 전용)을 따른다.
  queueRunnerState: QueueRunnerState | null;
  baseBranch: string | null;
  worktreeId: string | null;
  worktreeStatus: 'CREATING' | 'ACTIVE' | 'REMOVE_REQUESTED' | 'REMOVING' | 'REMOVED' | 'FAILED' | null;
  discoveredWorktreeId: string | null;
  userConventionIds: string[];
  createdAt: string;
  updatedAt: string;
  linkedCoAction: {
    id: string;
    title: string;
    content: string;
    source: 'MANUAL' | 'AUTO_SESSION';
    contentTokenCount: number | null;
    chainTotalTokenCount: number | null;
  } | null;
  linkedCoActionHistory: { id: string; title: string; content: string; createdAt: string } | null;
  isFavorite: boolean;
};

export type DaemonTriggerLog = {
  id: string;
  triggerId: string;
  level: 'INFO' | 'WARN' | 'ERROR';
  category?: DaemonTriggerLogCategory | null;
  toolName?: string | null;
  message: string;
  createdAt: string;
};

export type DaemonTriggerLogCategory = 'SYSTEM' | 'TEXT' | 'THINKING' | 'TOOL' | 'RESULT' | 'STDERR';

export type DaemonTriggerConventionSource = 'AUTO_MATCH' | 'PROMPT_REFERENCE' | 'USER_CUSTOM';

export type DaemonTriggerInjectedConvention = {
  id: string;
  conventionId: string;
  source: DaemonTriggerConventionSource;
  injectionOrder: number;
  createdAt: string;
  convention: {
    id: string;
    title: string;
    category: string;
    fileName: string | null;
    description: string | null;
    deletedAt: string | null;
  };
};

export type ActiveWorktree = {
  worktreeId: string;
  repositoryId: string | null;
  baseBranch: string | null;
  createdAt: string;
  alias: string | null;
};

export type WorktreeListItem = {
  worktreeId: string;
  worktreeStatus: string | null;
  worktreeError: string | null;
  repositoryId: string | null;
  baseBranch: string | null;
  daemonLabel: string | null;
  daemonOsType: string | null;
  triggerCount: number;
  createdAt: string;
  agentConfigName: string | null;
  alias: string | null;
  latestTriggerId: string;
};

// 외부 도구가 만든 발견(DISCOVERED) worktree. 로컬 절대 경로는 응답에 포함되지 않는다.
export type DiscoveredWorktreeListItem = {
  id: string;
  daemonId: string;
  daemonLabel: string | null;
  daemonOsType: string | null;
  repositoryId: string;
  repositoryName: string;
  repositoryRemoteUrl: string | null;
  branch: string | null;
  headSha: string | null;
  displayName: string | null;
  userLabel: string | null;
  status: 'AVAILABLE' | 'MISSING';
  lastSeenAt: string | null;
  createdAt: string;
};

export const getDiscoveredWorktreeDisplayName = (
  worktree: DiscoveredWorktreeListItem,
  idFallback: string = worktree.id,
) => worktree.userLabel ?? worktree.displayName ?? worktree.branch ?? idFallback;

export type ResolveDaemonTriggerSettingsInput = {
  agentConfigId: string;
  projectId?: string;
  targetDaemonId?: string | null;
  runnerType?: string;
  model?: string | null;
  fastMode?: boolean;
  effort?: string | null;
  planMode?: boolean;
  useWorktree?: boolean;
};

export type AgentConfig = {
  id: string;
  projectId?: string;
  name: string;
  osType?: OsType | null;
  targetDaemonId?: string | null;
  /** 이 에이전트를 등록한 머신 식별자. 러너가 자동 감지되었는지 판정하는 데 쓴다. */
  machineId?: string | null;
  createdByMemberId?: string | null;
  createdByName?: string | null;
  authPathFolderName?: string | null;
  /** 이 에이전트의 메인 체크아웃 폴더(러너가 보고한 정체성)와 그 폴더를 관측한 러너.
   *  실행 동시성 경고가 "같은 폴더인가"를 판정하는 축이다. 절대 경로도, 그 해시도 내려오지 않는다.
   *  미보고면 null이고, 그때는 서버와 같은 방향(직렬화 = 경고)으로 접힌다. */
  runnerCheckout?: { id: string; daemonId: string } | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  hasApiKey?: boolean;
  apiKeyExpiresAt?: string | null;
  apiKeyRevokedAt?: string | null;
  apiKeyLastUsedAt?: string | null;
  planModeOnly?: boolean;
  useWorktree?: boolean;
  shared?: boolean;
};

export type ProjectRepository = {
  id: string;
  projectId: string;
  name: string;
  provider: 'GITHUB' | 'GITLAB' | 'BITBUCKET' | 'LINEAR' | 'MANUAL';
  remoteUrl: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type ProjectRepositoryBranch = {
  name: string;
  commitSha: string | null;
};

export type RunnerModelOption = {
  label: string;
  value: string;
  maxInputTokens?: number | null;
  fastModeSupported: boolean;
  supportedEffortLevels?: string[];
};

export type RunnerModelRecord = {
  id: string;
  runnerType: RunnerType;
  label: string;
  value: string;
  isPublic: boolean;
  isEnabled: boolean;
  sortOrder: number;
  maxInputTokens: number | null;
  fastModeSupported: boolean;
  supportedEffortLevels: string[];
  createdAt: string;
  updatedAt: string;
};

export type MergedRunnerModelOption = RunnerModelOption & {
  isCustom?: boolean;
  availableDaemonIds?: string[] | null;
};

export type RunnerModelsByType = Record<RunnerType, RunnerModelOption[]>;
export type MergedRunnerModelsByType = Record<RunnerType, MergedRunnerModelOption[]>;
